import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Car, Users, ArrowRight, Zap } from "lucide-react";

/**
 * Price — App entry screen (cartoon/street palette).
 *
 * Mobile-first, no marketing website. Displays the Price wordmark, a bold
 * headline, and two chunky role tiles (passenger + driver) with the
 * comic-book / neubrutalism look (thick borders, hard offset shadows).
 */
export default function Landing() {
  const { user } = useAuth();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  if (user?.role === "driver") return <Navigate to="/dashboard/driver" replace />;
  if (user?.role === "client") return <Navigate to="/dashboard/client" replace />;
  if (user?.role === "hotel") return <Navigate to="/dashboard/hotel" replace />;
  if (user?.role === "admin") return <Navigate to="/admin" replace />;

  return (
    <div
      className="relative min-h-screen overflow-hidden text-foreground street-dots"
      style={{ background: "hsl(15 100% 96%)" }}
      data-testid="price-landing"
    >
      {/* Cartoon atmospheric blobs — coral + sunny yellow bursts */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, hsla(38, 95%, 62%, 0.45), transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-24 bottom-16 h-80 w-80 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, hsla(354, 88%, 66%, 0.38), transparent 70%)" }}
      />

      <div
        className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col"
        style={{
          paddingTop: "max(28px, env(safe-area-inset-top))",
          paddingBottom: "max(28px, env(safe-area-inset-bottom))",
          paddingLeft: "max(24px, env(safe-area-inset-left))",
          paddingRight: "max(24px, env(safe-area-inset-right))",
        }}
      >
        <header
          className={`mt-4 transition-all duration-700 ease-out ${ready ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"}`}
        >
          <div className="flex items-center gap-2.5">
            <div className="relative h-12 w-12 rounded-2xl bg-primary grid place-items-center rotate-[-6deg] street-border street-shadow-sm">
              <span className="font-[Outfit] text-2xl font-black text-primary-foreground">P</span>
              <span className="absolute -right-1.5 -bottom-1.5 h-3.5 w-3.5 rounded-full bg-accent street-border-thin" />
            </div>
            <span className="font-[Outfit] text-3xl tracking-tight font-black text-foreground">Price</span>
          </div>

          <div className="mt-8 inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold text-accent-foreground street-border-thin street-shadow-sm">
            <Zap className="h-3.5 w-3.5" /> Covoiturage
          </div>
          <h1 className="mt-5 font-[Outfit] text-[44px] font-black leading-[0.98] tracking-tight text-foreground">
            <span className="block">Partagez</span>
            <span className="block">la route.</span>
            <span
              className="mt-1 inline-block rounded-2xl bg-primary px-3 py-1 street-border street-shadow-sm text-primary-foreground rotate-[-2deg]"
            >
              Payez moins.
            </span>
          </h1>
          <p className="mt-5 max-w-sm font-[Outfit] text-[15px] leading-relaxed text-foreground/80">
            L&apos;app conviviale pour trouver un covoiturage ou proposer
            vos trajets. Choisissez votre côté 👇
          </p>
        </header>

        <div className="mt-8 flex-1 space-y-4">
          <RoleTile
            to="/register/client"
            label="Je cherche un covoiturage"
            sub="Réservez une place en 2 tapes."
            Icon={Users}
            bg="hsl(354 88% 66%)"
            fg="#fff"
            delay={120}
            ready={ready}
            testid="price-role-client"
          />
          <RoleTile
            to="/register/driver"
            label="Je suis covoitureur"
            sub="Proposez vos trajets, gagnez de l'argent."
            Icon={Car}
            bg="hsl(38 95% 62%)"
            fg="hsl(355 55% 12%)"
            delay={210}
            ready={ready}
            testid="price-role-driver"
          />
        </div>

        <footer
          className={`mt-8 flex flex-col items-center gap-3 transition-all duration-700 ease-out ${ready ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"}`}
          style={{ transitionDelay: ready ? "320ms" : "0ms" }}
        >
          <Link
            to="/login"
            className="street-press inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-white py-3.5 font-[Outfit] text-[15px] font-bold tracking-tight text-foreground street-border street-shadow-sm"
            data-testid="price-login"
          >
            Déjà inscrit ? Se connecter
            <ArrowRight className="h-4 w-4" />
          </Link>
        </footer>
      </div>
    </div>
  );
}

function RoleTile({ to, label, sub, Icon, bg, fg, delay, ready, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="street-press-lg group relative flex w-full items-center gap-4 overflow-hidden rounded-3xl bg-white p-4 street-border street-shadow"
      style={{
        transitionDelay: ready ? `${delay}ms` : "0ms",
        transform: ready ? "translateY(0)" : "translateY(12px)",
        opacity: ready ? 1 : 0,
      }}
    >
      <div
        className="relative grid h-16 w-16 flex-none place-items-center rounded-2xl street-border-thin"
        style={{ background: bg }}
      >
        <Icon className="h-7 w-7" style={{ color: fg }} strokeWidth={2.5} />
      </div>
      <div className="relative min-w-0 flex-1">
        <div className="font-[Outfit] text-[17px] font-black leading-tight tracking-tight text-foreground">
          {label}
        </div>
        <div className="mt-1 line-clamp-2 text-[13px] leading-snug text-foreground/60 font-medium">
          {sub}
        </div>
      </div>
      <ArrowRight className="relative h-5 w-5 flex-none text-foreground/50 transition-all duration-200 group-hover:translate-x-1 group-hover:text-primary" />
    </Link>
  );
}

export const PublicLanding = Landing;
